import { openAlbum as JSBOpenAlbum } from '@bridge/sharee';
import { FieldProps } from '../type/form';
import React, { useContext, useEffect, useRef, useState } from 'react';
import PlusFill from 'icon/plus_fill.svg';
import XMarkFill from 'icon/x_mark_fill.svg';
import { Button } from '@lemon8/design';
import { ButtonSize } from '@lemon8/design/src/components/Button/button';
import { UploadImageProps, useFetchUploadImage } from '~/utils/fetch/use-upload-image';
import logger from '~/utils/logger';
import { FormContext, FormItem } from '../../form';
import { useAtomValue, useSetAtom } from 'jotai';
import { $UserAgent } from '@lemon8/web-app-shared/atom';
import { $ToastAtom } from '~/atoms/simple-toast';

import './style.scss';

const MAX_ERROR_TIMES = 3;

// eslint-disable-next-line max-lines-per-function
export function FormFile(props: FieldProps) {
  const {
    name,
    label,
    tip,
    prefix,
    prompt,
    className,
    required,
    rules,
    group,
    buttonText,
    isImgFile = true,
    defaultValue = [],
  } = props;

  const { isInApp } = useAtomValue($UserAgent);

  const [imgFileList, setImgFileList] = useState<any[]>([]);
  const inputFileRef = useRef<HTMLInputElement>(null);
  const { dispatch } = useContext(FormContext);

  const fetchUploadImage = useFetchUploadImage();

  const errTimesRef = useRef(0);

  const showToast = useSetAtom($ToastAtom);

  // 初始化
  useEffect(() => {
    setImgFileList(defaultValue as any);
    dispatch({ name, type: 'change', payload: defaultValue });
  }, []);

  useEffect(() => {
    dispatch({ name, type: 'change', payload: imgFileList });
  }, [imgFileList, name, dispatch]);

  // 判断图片格式
  function checkImgType(file: File) {
    if (!/\.(jpg|jpeg|png|gif|GIF|JPG|PNG)$/.test(file.name)) {
      return false;
    } else {
      return true;
    }
  }

  // 上传图片后处理
  async function handleImageUpload(payload: UploadImageProps, fileName: string) {
    try {
      const { url, uri } = await fetchUploadImage(payload);

      // 上传成功后将url替换掉
      setImgFileList((list) =>
        list.map((img) =>
          img.fileName === fileName
            ? {
                fileName,
                fileUrl: url,
                tosURI: uri,
              }
            : img,
        ),
      );
      if (inputFileRef?.current) {
        // 上传后，将文件选择框内的文件删掉，可以保证用户在删掉文件后可以重复的进行上传
        inputFileRef.current.value = '';
      }

      errTimesRef.current = 0;
    } catch (err) {
      logger.error(err, 'form file fetch upload image error');

      if (errTimesRef.current > MAX_ERROR_TIMES) {
        // 上传失败了，删掉图片
        const index = imgFileList.findIndex((item) => item?.fileName === fileName) ?? -1;
        handleClose(index);

        showToast({
          time: Date.now(),
          content: 'Image upload failed, please try again.',
        });

        return;
      }

      errTimesRef.current++;

      // 上传出错重新传一次
      handleImageUpload(payload, fileName);
    }
  }

  // 如果是端内的话，调起相册
  async function openAlbum() {
    if (isInApp) {
      const data = await JSBOpenAlbum();
      const { image } = data;
      if (image) {
        const fileName = `${name}_${Date.now()}.png`;
        // 首先为了展示，先将name传入，url为''，上传成功后将url替换
        setImgFileList((list) => [
          {
            fileName,
            fileUrl: '',
          },
          ...list,
        ]);
        handleImageUpload(
          {
            base64Image: image,
            isInApp,
          },
          fileName,
        );
      }
    }
  }

  // web采用的是input file，监听file的改变
  function handleFileChange(event: any) {
    const { files } = event.target;
    if (files.length > 0) {
      if (!isImgFile || checkImgType(files[0])) {
        const fileName = `${name}_${Date.now()}.png`;
        // 首先为了展示，先将name传入，url为''，上传成功后将url替换
        setImgFileList((list) => [
          {
            fileName,
            fileUrl: '',
          },
          ...list,
        ]);
        handleImageUpload(
          {
            image: files[0] as File,
            isInApp,
          },
          fileName,
        );
      }
    }
  }

  /** 删除文件 */
  function handleClose(index: number) {
    setImgFileList([...imgFileList.slice(0, index), ...imgFileList.slice(index + 1)]);
  }

  return (
    <FormItem
      {...{
        name,
        label,
        tip,
        prefix,
        prompt,
        className,
        required,
        group,
        rules,
      }}
    >
      <div className="upload-container">
        <div className="upload-button">
          <Button
            className="upload-button-container"
            title={buttonText}
            variant={'Primary'}
            size={ButtonSize.medium}
            icon={<PlusFill className="upload-button-icon" />}
          />
          {/* <div className="upload-button-container">
            <PlusFill className="plus-icon" />
            {buttonText}
          </div> */}
          {isInApp ? (
            <input type="button" className={'form-file'} ref={inputFileRef} onClick={openAlbum} />
          ) : (
            <input
              type="file"
              onChange={handleFileChange}
              className={'form-file'}
              ref={inputFileRef}
              accept="image/jpeg,image/jpg,image/png"
            />
          )}
        </div>
        <div className="upload-list">
          {imgFileList.map((item, index) => {
            return (
              <div className="upload-item" key={item.fileName}>
                <div className="upload-item-name">{item.fileName}</div>
                <XMarkFill className="x-mark-icon" onClick={() => handleClose(index)} />
              </div>
            );
          })}
        </div>
      </div>
    </FormItem>
  );
}
